import { useEffect, useRef, useState } from "react";
import { Paper, Button, Avatar, useMediaQuery } from "@material-ui/core";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import { CellValueChangedEvent, GridReadyEvent } from "ag-grid-community";
import { useCallback } from "react";
import { ClaimTransaction } from "../../../../../../../@types/claimList.types";
import { useContext } from "react";
import { Suspense } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { dateTimeZoneConverter } from "../../../../../../../utils/commonFunctions/date";
import { api } from "../../../../../../../utils/api";
import { AuthContext, UIContext } from "../../../../../../../contexts";
import { Member } from "../../../../../../../@types/member.types";
import { ClaimListQuery } from "../../../../../../../utils/api/claim/claimList";

import "./claimList.css";
import { trackPromise } from "react-promise-tracker";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../../../constants/caption/dashboardHeader";

type PDDT = {
	value: string;
};

type CheckNumber = {
	value: number | null;
};

type EOBClickProps = {
	_: unknown;
	_currentClaims: ClaimTransaction[];
	_historyClaims: ClaimTransaction[];
};

const ClaimList = ({ member }: { member: Member }): JSX.Element => {
	const [currentClaims, setCurrentClaims] = useState<ClaimTransaction[]>([]);
	const [memberAuthToken, setMemberAuthToken] = useState<string | null>(null);
	const { setDashboardHeader } = useContext(UIContext);
	const [currentClaims1, setCurrentClaims1] = useState([]);
	const [dependent, setDependent] = useState<Member>();
	const [currentClaims2, setCurrentClaims2] = useState([]);
	const location = useLocation();
	const [historyClaims, setHistoryClaims] = useState<ClaimTransaction[]>([]);
	const [claimsFilterSchema, setClaimsFilterSchema] = useState({
		CLMPRE: { value: null, type: null },
		CLMNO: { value: null, type: null },
		CLMSUF: { value: null, type: null },
		PDDT: { value: null, type: null },
		PRVCKNO: { value: null, type: null },
		EECKNO: { value: null, type: null }
	});
	const [avatarInitials, setAvatarInitials] = useState<string>("");
	const [limit, setLimit] = useState<any | null>(null);
	const [pagination, setPagination] = useState({
		currentPage: 1,
		pageCount: 11
	});
	const navigate = useNavigate();
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const agGridRef = useRef<any>(null);

	const handleAgGridReady = (params: GridReadyEvent) => {
		params.api.sizeColumnsToFit();
	};

	const getClaimList = useCallback(async (): Promise<void> => {
		if (
			member === null ||
			member === undefined ||
			member?.dependent_number === null ||
			member._id === undefined ||
			member.employee_number === null ||
			member.employee_number === undefined ||
			localStorage.getItem("@jwt") === null ||
			localStorage.getItem("@jwt") === undefined
		) {
			return;
		}
		const token = localStorage.getItem("@jwt");
		if (token === null) {
			return;
		}
		const redirectionResult = await trackPromise(api.redirection.redirectUser(member._id, token));

		const claimListQuery: ClaimListQuery = {
			dependent_number: member.dependent_number
		};
		const claims = await trackPromise(api.claim.getClaimList(claimListQuery, redirectionResult.token));
		if (claims) {
			setCurrentClaims(Object.assign([], claims.currentClaims));
			setHistoryClaims(Object.assign([], claims.historyClaims));
		}
	}, [member]);

	const PDDTCellRenderer = ({ value }: PDDT) => {
		return <>{dateTimeZoneConverter(value as string) as string}</>;
	};

	const checkIfClaimExists = (claims: ClaimTransaction[], claim: ClaimTransaction): boolean => {
		let claimExists = false;
		for (let eachClaimIdx = 0; eachClaimIdx < claims.length; eachClaimIdx++) {
			const { CLMPRE, CLMNO, CLMSUF } = claims[eachClaimIdx];
			// console.log("clmno", CLMNO, claim.CLMNO);
			if (CLMPRE === claim.CLMPRE && CLMNO === claim.CLMNO && CLMSUF === claim.CLMSUF) {
				claimExists = true;
				break;
			}
		}
		return claimExists;
	};

	const handleEOBClick = useCallback(
		(_currentClaims: ClaimTransaction[], _historyClaims: ClaimTransaction[]) => {
			if (agGridRef?.current) {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const focusedCell = agGridRef.current!.api!.getFocusedCell();
				const row = agGridRef.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
				const { data } = row;
				const { CLMNO, CLMSUF, CLMPRE } = data;
				let type = -1;
				if (checkIfClaimExists(_currentClaims, data)) {
					type = 0;
				} else if (checkIfClaimExists(_historyClaims, data)) {
					type = 1;
				}

				navigate(
					`/employer/claim/EOB?type=${type}&&number=${CLMNO}&&suffix=${CLMSUF}&&pre=${CLMPRE}&&gn=${member.group_number}`,
					{
						state: {
							member: member
						}
					}
				);
			}
		},
		[member, navigate]
	);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const actionCellRender = ({ _, _currentClaims, _historyClaims }: EOBClickProps) => {
		//console.log("_currentClaims", _currentClaims);
		return (
			<Button
				className="ag-grid-cell-button"
				variant="outlined"
				onClick={() => handleEOBClick(_currentClaims, _historyClaims)}
			>
				EOB
			</Button>
		);
	};

	const checkNumberCellRender = ({ value }: CheckNumber) => {
		return <>{value ? value : "N/A"}</>;
	};

	const functionToBePassed = () => {
		// console.log("I am functionToBePassed");
	};

	const getAvatarInitials = useCallback(() => {
		if (member?.group_number && member?.first_name && member?.last_name) {
			setAvatarInitials(member.first_name[0] + member.last_name[0]);
		}
	}, [member]);

	useEffect(() => {
		getClaimList();
		getAvatarInitials();
	}, [getAvatarInitials, getClaimList]);
	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.claims);
	}, [setDashboardHeader]);

	if (!member) {
		return <></>;
	}

	return (
		<div className="claim-list">
			<div className="theme-profile-header">
				{/* <div className="theme-name">
					{dependent
						? dependent?.first_name + " " + dependent?.last_name
						: member?.first_name + " " + member?.last_name}
				</div> */}
				{/* <div className="theme-avatar-meta-cont">
						<Avatar className="theme-avatar">{avatarInitials}</Avatar>
						<div className="theme-assign-info">
							<div className="theme-group-name">{member?.group?.name}</div>
							<div className="theme-location-name">{member?.location_name}</div>
						</div>
					</div> */}
			</div>
			<div className="ag-theme-alpine" style={{ height: "400px", width: "100%" }}>
				<AgGridReact
					ref={agGridRef}
					rowData={[...currentClaims, ...historyClaims]}
					onGridReady={handleAgGridReady}
					animateRows
					rowDragManaged
					defaultColDef={{
						filter: "agTextColumnFilter",
						floatingFilter: true,
						resizable: true,
						floatingFilterComponentParams: { suppressFilterButton: true }
					}}
					defaultColGroupDef={{ marryChildren: true }}
					rowSelection="single"
					enableCellChangeFlash={true}
					frameworkComponents={{
						PDDTCellRenderer: PDDTCellRenderer,
						checkNumberCellRender: checkNumberCellRender,
						actionCellRender: actionCellRender,
						schema: claimsFilterSchema,
						functionToBePassed: functionToBePassed
					}}
					onCellValueChanged={(params: CellValueChangedEvent) => {
						params.api.refreshCells({
							force: true,
							columns: ["actionCellRenderer"]
						});
					}}
				>
					<AgGridColumn field="CLMPRE" headerName="Claim Prefix"></AgGridColumn>
					<AgGridColumn field="CLMNO" headerName="Claim Number"></AgGridColumn>
					<AgGridColumn field="CLMSUF" headerName="Claim Suffix"></AgGridColumn>
					<AgGridColumn field="PDDT" headerName="Paid Date" cellRenderer="PDDTCellRenderer"></AgGridColumn>
					<AgGridColumn
						field="PRVCKNO"
						headerName="Provider Check No"
						cellRenderer="checkNumberCellRender"
					></AgGridColumn>
					<AgGridColumn
						field="EECKNO"
						headerName="Employee Check No"
						cellRenderer="checkNumberCellRender"
					></AgGridColumn>
					<AgGridColumn
						field="action"
						headerName="Action"
						cellRenderer="actionCellRender"
						cellRendererParams={{ _currentClaims: currentClaims, _historyClaims: historyClaims }}
						filter={false}
					></AgGridColumn>
				</AgGridReact>
			</div>
		</div>
	);
};

export default ClaimList;
